import type { NoteComposeRequest, PerformanceNotesView } from '@tunasong/plugin-lib'
import type { RootState } from '@tunasong/redux'
import { features } from '@tunasong/redux'
import type { Entity, Persisted } from '@tunasong/schemas'
import { isSong } from '@tunasong/schemas'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { usePerformanceConfig } from './part/performance-config.hook.js'
import { sectionInView } from './section-in-view.js'

export interface PerformanceNotesOptions {
  isTiny: boolean
  entity: Persisted<Entity> | null | undefined
  scrollEl: HTMLElement | null
  /** Pauses the view's autoscroll while a composer is open; a view without autoscroll passes a no-op. */
  holdAutoscroll: () => () => void
}

/**
 * The performer's notes in Perform, shared by the song and the playlist views: where they show, the
 * request to write one (N, "+ Jot"), and the slot above the title for song notes.
 */
export const usePerformanceNotes = ({ isTiny, entity, scrollEl, holdAutoscroll }: PerformanceNotesOptions) => {
  const { config, updatePerformanceConfig } = usePerformanceConfig()
  const [compose, setCompose] = useState<NoteComposeRequest | null>(null)
  const [songNotesSlot, setSongNotesSlot] = useState<HTMLElement | null>(null)

  const visible = config.notesVisible ?? false
  /** On a phone a song's notes go inline under each section label: a panel would cover the chart. */
  const placement = isTiny && isSong(entity) ? 'beside' : (config.notesPlacement ?? 'beside')

  const setVisible = (notesVisible: boolean) => updatePerformanceConfig({ notesVisible })

  /**
   * A tapped note marker sets `pendingAnchoredNoteId`: notes turn on so the note shows. The panel
   * expands that note and clears the id; beside the song the note is already on screen, so the id is
   * cleared here — left set, it turned the notes straight back on after Shift+N.
   */
  const dispatch = useDispatch()
  const pendingAnchoredNoteId = useSelector((state: RootState) => state.ui.pendingAnchoredNoteId)
  useEffect(() => {
    if (!pendingAnchoredNoteId) {
      return
    }
    if (!visible) {
      updatePerformanceConfig({ notesVisible: true })
    }
    if (placement === 'beside') {
      dispatch(features.ui.actions.setPendingAnchoredNoteId(null))
    }
  }, [pendingAnchoredNoteId, visible, placement, updatePerformanceConfig, dispatch])

  /** Writing a note means wanting to see it: notes turn on first. */
  const requestNote = (via: 'jot' | 'key') => {
    if (!visible) {
      setVisible(true)
    }
    const inline = via === 'key' && !isTiny && placement === 'beside'
    setCompose({ sectionId: sectionInView(scrollEl), surface: inline ? 'inline' : 'sheet' })
  }

  const notes: PerformanceNotesView = {
    visible,
    placement,
    compose,
    clearCompose: () => setCompose(null),
    requestCompose: setCompose,
    holdAutoscroll,
    songNotesSlot,
  }

  return { notes, showPanel: visible && placement === 'panel', setVisible, requestNote, setSongNotesSlot }
}
